/**
 * PermissionsMatrix  —  Phase 76.2.G
 *
 * Dynamic, data-driven permissions grid rendered inside the Team Management
 * dialogs (invite + edit). Each row corresponds to one module from
 * `modules_v76` and renders STRICTLY the role options associated with that
 * module via `module_roles_v76.module_key`, alongside a base `None` / `ללא`
 * option that revokes access.
 *
 * ─── Why per-row dynamic columns? (Phase 76.2.G architectural shift) ─────────
 *
 * Phase 76.2.C..F shipped a uniform 4-column grid hardcoded around the legacy
 * level enum `none|viewer|editor|admin`. That violated the core domain
 * invariant: every module has a DISTINCT authorization schema (Cashflow only
 * exposes Viewer + Admin; HR/Users only exposes Admin; future regional or
 * purchasing modules will introduce arbitrary custom roles). The matrix is
 * now genuinely data-driven — it reads the per-module role catalog at runtime
 * and renders only the cells that exist for that module.
 *
 * ─── Layout strategy ────────────────────────────────────────────────────────
 *
 * Rows use a `flex` layout (NOT a fixed grid) so each row independently sizes
 * its option buttons to whatever roles its module actually owns:
 *   • Module label cell on the leading edge with a fixed min-width.
 *   • Per-row option buttons (None + N module roles) flex-grow uniformly so
 *     a 2-role module reads as two wide buttons and a 4-role module reads
 *     as four narrower buttons — graceful at any column count.
 *   • In RTL (Hebrew), the natural flex direction places the label on the
 *     right correctly.
 *
 * ─── Controlled value contract (UNCHANGED from 76.2.C) ──────────────────────
 *
 * The matrix continues to emit `Record<string, PermissionLevel>` keyed by
 * module_key, where `PermissionLevel = 'admin'|'editor'|'viewer'|'none'`.
 * Each rendered role button derives its emitted level from the SUFFIX of
 * the canonical `role_key` (`${module}.${level}` per the seed convention).
 * Roles whose suffix is outside the legacy enum are skipped at render time
 * with a console warning — when a future migration introduces custom role
 * suffixes the persistence layer (`ModuleRoleLevel` in teamService) must be
 * widened in lockstep before the UI will surface them.
 *
 * Unsubscribed module rows are greyed out and force their selection to
 * 'none' as a defense-in-depth UI guard (mirroring the BEFORE INSERT/UPDATE
 * trigger from Phase 76.2.F).
 */

import { useTranslation } from 'react-i18next';
import { Lock, CircleAlert as AlertCircle, RefreshCw } from 'lucide-react';
import { useTeamMatrixData, type MatrixRole } from './useTeamMatrixData';

// ── Types ─────────────────────────────────────────────────────────────────────

export type PermissionLevel = 'admin' | 'editor' | 'viewer' | 'manager' | 'employee' | 'none';

export type PermissionsMatrixValue = Record<string, PermissionLevel>;

interface PermissionsMatrixProps {
  /** Active company id (used to load subscriptions for grey-out). */
  companyId: string | null;
  /** Controlled value, keyed by module key. */
  value:     PermissionsMatrixValue;
  /** Controlled change handler. */
  onChange:  (next: PermissionsMatrixValue) => void;
  /** Optional: disable the whole matrix (e.g. while submitting). */
  disabled?: boolean;
}

// ── Constants ─────────────────────────────────────────────────────────────────

/** Suffixes from `module_roles_v76.role_key` that the persistence layer
 *  (`teamService.ModuleRoleLevel`) accepts. Roles with other suffixes are
 *  filtered at render time. */
const LEGACY_LEVELS = ['admin', 'editor', 'viewer', 'manager', 'employee'] as const;
type LegacyLevel = typeof LEGACY_LEVELS[number];

function isLegacyLevel(s: string): s is LegacyLevel {
  return (LEGACY_LEVELS as readonly string[]).includes(s);
}

// Fallback emoji map, identical to SuperAdminDashboard so the two surfaces stay
// visually consistent.
const MODULE_EMOJI: Record<string, string> = {
  cashflow:  '📊',
  hr:        '👥',
  inventory: '📦',
  users:     '👥',
};

// ── Component ─────────────────────────────────────────────────────────────────

export function PermissionsMatrix({
  companyId,
  value,
  onChange,
  disabled = false,
}: PermissionsMatrixProps) {
  const { t, i18n }  = useTranslation();
  const isHebrew     = i18n.language?.toLowerCase().startsWith('he') ?? false;

  const { modules, roles, loading, error, refresh } = useTeamMatrixData(companyId);

  // ── Loading / empty / error states ──────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-400 gap-2 text-sm">
        <RefreshCw className="w-4 h-4 animate-spin" />
        <span>{t('team.permissionsMatrix.loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-start gap-3 p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
        <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-red-500" />
        <div className="flex-1 min-w-0">
          <p className="font-medium">{t('team.permissionsMatrix.loadError')}</p>
          <pre className="text-red-500 mt-0.5 whitespace-pre-wrap text-xs">{error}</pre>
        </div>
        <button
          type="button"
          onClick={() => void refresh()}
          className="px-2 py-1 text-xs rounded-md bg-white border border-red-200 text-red-700 hover:bg-red-100"
        >
          {t('team.permissionsMatrix.retry')}
        </button>
      </div>
    );
  }

  if (modules.length === 0) {
    return (
      <div className="py-6 text-center text-sm text-gray-400">
        {t('team.permissionsMatrix.noModules')}
      </div>
    );
  }

  // ── Cell click handler ──────────────────────────────────────────────────────
  const handleCellClick = (moduleKey: string, level: PermissionLevel) => {
    if (disabled) return;
    const next: PermissionsMatrixValue = { ...value, [moduleKey]: level };
    onChange(next);
  };

  // ── Role lookup helpers ─────────────────────────────────────────────────────
  /**
   * Resolve the per-module role option list. Each role is paired with its
   * extracted legacy-level suffix so the matrix can keep emitting the narrow
   * `PermissionLevel` value contract while surfacing the role's
   * dual-locale display name in the UI.
   */
  function optionsForModule(moduleKey: string): Array<{
    role:  MatrixRole;
    level: LegacyLevel;
  }> {
    return roles
      .filter(r => r.module_key === moduleKey)
      .map(r => {
        // role_key convention: `${module_key}.${level}` — strip the module
        // prefix to obtain the persistence-layer suffix.
        const suffix = r.role_key.startsWith(`${moduleKey}.`)
          ? r.role_key.slice(moduleKey.length + 1)
          : r.role_key;
        return { role: r, suffix };
      })
      .filter((opt): opt is { role: MatrixRole; suffix: LegacyLevel } => {
        if (!isLegacyLevel(opt.suffix)) {
          // Defensive: a custom role suffix (e.g. 'cashflow.purchasing') would
          // be unwritable through the current teamService.ModuleRoleLevel
          // narrow union. Skip rather than render an option that would 500
          // on save. When custom suffixes ship, widen ModuleRoleLevel and
          // drop this filter.
          if (typeof window !== 'undefined') {
            // eslint-disable-next-line no-console
            console.warn(
              `[PermissionsMatrix] role_key '${opt.role.role_key}' has a non-legacy ` +
              `suffix '${opt.suffix}'. Hiding until teamService.ModuleRoleLevel is widened.`,
            );
          }
          return false;
        }
        return true;
      })
      .map(opt => ({ role: opt.role, level: opt.suffix }))
      // sort_order already applied at the SELECT in useTeamMatrixData; this is
      // a defensive re-sort in case future filters reorder the input.
      .sort((a, b) => a.role.sort_order - b.role.sort_order);
  }

  function selectedClassesFor(level: PermissionLevel): string {
    switch (level) {
      case 'none':     return 'bg-gray-200 text-gray-700 ring-2 ring-gray-300';
      case 'viewer':   return 'bg-blue-100 text-blue-700 ring-2 ring-blue-300';
      case 'editor':   return 'bg-amber-100 text-amber-700 ring-2 ring-amber-300';
      case 'manager':  return 'bg-amber-100 text-amber-700 ring-2 ring-amber-300';
      case 'employee': return 'bg-sky-100 text-sky-700 ring-2 ring-sky-300';
      case 'admin':    return 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-300';
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">
        {t('team.permissionsMatrix.hint')}
      </p>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Header strip — generic since per-row column counts vary. */}
        <div className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wide">
          <span className="flex-shrink-0" style={{ minWidth: 140 }}>
            {t('team.permissionsMatrix.moduleColumn')}
          </span>
          <span className="flex-1 text-center">
            {t('team.permissionsMatrix.rolesColumn')}
          </span>
        </div>

        {/* Body rows — one per module, each flex-laid-out independently. */}
        {modules.map(mod => {
          const labelHe   = mod.display_name_he;
          const labelEn   = mod.display_name_en;
          const labelMain = isHebrew ? labelHe : labelEn;
          const icon      = mod.icon ?? MODULE_EMOJI[mod.key] ?? '🔌';

          // Per-module role catalog — strictly the roles whose
          // module_key === this module's key. The 'None' option is prepended
          // below to enable revocation.
          const moduleOptions = optionsForModule(mod.key);

          // Effective level: force 'none' for unsubscribed modules regardless of
          // any stale value coming in (defense-in-depth UI guard).
          const rawLevel: PermissionLevel = value[mod.key] ?? 'none';
          const effectiveLevel: PermissionLevel = mod.subscribed ? rawLevel : 'none';

          const rowDisabled = disabled || !mod.subscribed;

          // Dual-locale tooltip on the module label cell.
          const tooltipBase   = `${labelHe} • ${labelEn}`;
          const tooltipSuffix = mod.subscribed
            ? ''
            : ` — ${t('team.permissionsMatrix.unsubscribedTooltip')}`;
          const moduleTooltip = `${tooltipBase}${tooltipSuffix}`;

          return (
            <div
              key={mod.key}
              className={`
                flex flex-wrap items-center gap-3 px-4 py-3 border-b border-gray-100 last:border-b-0
                ${rowDisabled ? 'opacity-50' : 'hover:bg-gray-50/60'}
                transition-colors
              `}
            >
              {/* Module label cell — fixed width on the leading edge. */}
              <div
                className="flex items-center gap-2 min-w-0 flex-shrink-0"
                style={{ minWidth: 140, maxWidth: 200 }}
                title={moduleTooltip}
              >
                <span className="text-lg leading-none flex-shrink-0">{icon}</span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {labelMain}
                  </p>
                  {!mod.subscribed && (
                    <p className="flex items-center gap-1 text-[10px] text-gray-400 mt-0.5">
                      <Lock className="w-3 h-3" />
                      {t('team.permissionsMatrix.unsubscribedBadge')}
                    </p>
                  )}
                </div>
              </div>

              {/* Option buttons — [None, ...rolesForModule]. flex-1 so the
                  option strip expands to fill the row width regardless of
                  how many options this module exposes. */}
              <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0 justify-end">
                {/* Base 'None' option — always present per-row to allow
                    revoking access even on modules with custom role sets. */}
                {(() => {
                  const lvl: PermissionLevel = 'none';
                  const isSelected = effectiveLevel === lvl;
                  const noneLabel = t('team.permissionsMatrix.noneOption');
                  const cellTooltip = mod.subscribed
                    ? `${labelHe} • ${labelEn} — ${noneLabel}`
                    : `${labelHe} • ${labelEn} — ${t('team.permissionsMatrix.unsubscribedTooltip')}`;
                  return (
                    <button
                      key="__none__"
                      type="button"
                      onClick={() => handleCellClick(mod.key, lvl)}
                      disabled={rowDisabled}
                      title={cellTooltip}
                      aria-pressed={isSelected}
                      className={`
                        flex-1 min-w-[90px] max-w-[160px] px-2.5 py-1.5 rounded-md text-xs font-medium
                        transition-all duration-150
                        ${rowDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                        ${isSelected
                          ? selectedClassesFor(lvl)
                          : 'bg-white border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700'}
                      `}
                    >
                      {noneLabel}
                    </button>
                  );
                })()}

                {/* Per-module role buttons — dynamic count, derived from
                    `module_roles_v76` filtered by module_key. */}
                {moduleOptions.length === 0 ? (
                  <span className="flex-1 text-center text-[11px] text-gray-400 italic">
                    {t('team.permissionsMatrix.noRolesForModule')}
                  </span>
                ) : (
                  moduleOptions.map(({ role, level }) => {
                    const isSelected = effectiveLevel === level;
                    // Prefer the role's dual-locale display name from
                    // module_roles_v76; fall back to the localized legacy
                    // label so existing screenshots / muscle memory carry
                    // over when display names aren't seeded.
                    const localizedRoleName = isHebrew
                      ? role.display_name_he
                      : role.display_name_en;
                    const fallbackLabel = t(`team.invite.permissionLevels.${level}`);
                    const roleLabel = localizedRoleName && localizedRoleName !== role.role_key
                      ? localizedRoleName
                      : fallbackLabel;
                    const cellTooltip = mod.subscribed
                      ? `${labelHe} • ${labelEn} — ${role.display_name_he} • ${role.display_name_en}`
                      : `${labelHe} • ${labelEn} — ${t('team.permissionsMatrix.unsubscribedTooltip')}`;

                    return (
                      <button
                        key={role.id}
                        type="button"
                        onClick={() => handleCellClick(mod.key, level)}
                        disabled={rowDisabled}
                        title={cellTooltip}
                        aria-pressed={isSelected}
                        className={`
                          flex-1 min-w-[90px] max-w-[160px] px-2.5 py-1.5 rounded-md text-xs font-medium
                          transition-all duration-150
                          ${rowDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                          ${isSelected
                            ? selectedClassesFor(level)
                            : 'bg-white border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700'}
                        `}
                      >
                        {roleLabel}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PermissionsMatrix;
